import { graphqlOperation } from "aws-amplify";

/**
 * Build one document that asks the same field N times under distinct aliases.
 * Returns { query, variables, aliases } so the caller can map results back.
 *
 * Moved verbatim from PickList.vue.
 */
export function buildBatch(opName, field, argName, argType, ids, selection) {
  const varDefs = ids.map((_, i) => `$v${i}: ${argType}`).join(", ");
  const body = ids
    .map((_, i) => `a${i}: ${field}(${argName}: $v${i}${selection.connection ? ", limit: 200" : ""}) { ${selection.body} }`)
    .join("\n    ");
  const variables = {};
  ids.forEach((id, i) => { variables["v" + i] = id; });
  return {
    query: `query ${opName}(${varDefs}) {\n    ${body}\n  }`,
    variables,
    aliases: ids.map((id, i) => ["a" + i, id]),
  };
}

/** Moved verbatim from PickList.vue. */
export function chunk(arr, n) {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

/** Fan a per-id query out in aliased batches. Returns { id: result }. */
export async function batched(api, opName, field, argName, argType, ids, selection, opts = {}) {
  const { batchSize = 25, onTruncated } = opts;
  const out = {};
  const groups = chunk([...new Set(ids)], batchSize);
  // Batches run concurrently; each batch is one HTTP request.
  const results = await Promise.all(
    groups.map(async (group, gi) => {
      const { query, variables, aliases } = buildBatch(opName + gi, field, argName, argType, group, selection);
      const res = await api.graphql(graphqlOperation(query, variables));
      return { aliases, data: res.data };
    })
  );
  for (const { aliases, data } of results) {
    for (const [alias, id] of aliases) {
      const value = data[alias];
      if (selection.connection && value && value.nextToken && onTruncated) onTruncated(field, id);
      out[id] = value;
    }
  }
  return out;
}

export async function fetchAll(api, query, variables, field) {
  const items = [];
  let nextToken = null;
  do {
    const res = await api.graphql(graphqlOperation(query, { ...variables, nextToken }));
    const page = res.data[field];
    items.push(...(page.items || []).filter(Boolean));
    nextToken = page.nextToken;
  } while (nextToken);
  return items;
}
